// src/pages/Interactions.js
import { useState, useMemo, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import api from '../lib/api'
import { firmNames } from '../lib/contactFirms'
import { useRoleOptions } from '../lib/roles'
import { EntityField, EntityMultiField } from '../components/EntityPicker'
import { PIPELINE_STAGES, PL_BADGE } from '../lib/pipelineStages'

// ── Constants ─────────────────────────────────────────────────────────────

const INT_TYPES   = ['Email', 'Meeting', 'Call', 'Note', 'Event']
const INT_SOURCES = ['Manual', 'Gmail Sync', 'Calendar Sync', 'Outlook Sync']
const DIRECTIONS  = ['Inbound', 'Outbound']
const PAGE_SIZE    = 50
// Upcoming isn't paged; the API caps a request at 200.
const UPCOMING_LIMIT = 200
// Section labels over the Upcoming / Past tables.
const SECTION_HEADING_STYLE = {
  fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
  color: 'var(--text-secondary)', margin: '0 0 10px',
}

const TYPE_BADGE = {
  'Email': 'badge-blue', 'Meeting': 'badge-purple',
  'Call': 'badge-green', 'Note': 'badge-gray', 'Event': 'badge-yellow',
}
const SOURCE_BADGE = {
  'Manual': 'badge-gray', 'Gmail Sync': 'badge-red',
  'Calendar Sync': 'badge-blue', 'Outlook Sync': 'badge-purple',
}
const DIR_BADGE = {
  'Inbound': 'badge-green', 'Outbound': 'badge-blue',
}

const TODAY = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d })()

// ── Helpers ───────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
function formatDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}
function isOverdue(dateStr) {
  if (!dateStr) return false
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0)
  return d <= TODAY
}

// Builds a compact page-number list with '…' gaps, e.g. [1, '…', 4, 5, 6, '…', 20]
function pageList(current, total) {
  const delta = 1
  const range = []
  for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) range.push(i)
  if (range[0] > 1) { if (range[0] > 2) range.unshift('…'); range.unshift(1) }
  if (range[range.length - 1] < total) { if (range[range.length - 1] < total - 1) range.push('…'); range.push(total) }
  return range
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, marginTop: 16 }}>
      <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>‹ Prev</button>
      {pageList(page, totalPages).map((p, idx) => p === '…' ? (
        <span key={`gap-${idx}`} style={{ padding: '0 4px', color: 'var(--text-muted)', fontSize: 12 }}>…</span>
      ) : (
        <button
          key={p}
          className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => onChange(p)}
          style={{ minWidth: 32 }}
        >{p}</button>
      ))}
      <button className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Next ›</button>
    </div>
  )
}

// ── Shared UI ─────────────────────────────────────────────────────────────

function Modal({ title, onClose, width = 540, zIndex = 1000, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: width,
        maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button className="btn-icon" onClick={onClose} style={{ fontSize: 16 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function ErrorBanner({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 6, padding: '10px 14px', fontSize: 13, color: 'var(--danger-text)',
    }}>{msg}</div>
  )
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  )
}

function DeleteConfirmModal({ name, onCancel, onConfirm, deleting }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1200, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
      }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Delete {name}?</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
          Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{name}</strong>?
          This action cannot be undone.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{children || '—'}</div>
    </div>
  )
}

// ── Add-contact typeahead (used inside the detail modal) ──────────────────
// Searchable input that filters `contacts` client-side. Selecting calls
// onAdd(contactId) and stays open (input clears) so several can be added in
// a row. When the typed name has no exact match, a "Create new contact"
// option appears (calls onCreateNew(name)). Escape or blur calls onClose.
function AddContactTypeahead({ contacts, onAdd, onCreateNew, onClose }) {
  const [query, setQuery]         = useState('')
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const typed   = query.trim()
  const q       = typed.toLowerCase()
  const matches = (q ? contacts.filter(c => c.name.toLowerCase().includes(q)) : contacts).slice(0, 50)
  // Offer "create" whenever there's text and no exact (case-insensitive) match
  const hasExact   = q && contacts.some(c => c.name.trim().toLowerCase() === q)
  const showCreate = typed.length > 0 && !hasExact
  const createIdx  = matches.length            // create row sits after matches
  const rowCount   = matches.length + (showCreate ? 1 : 0)

  function pick(idx) {
    if (showCreate && idx === createIdx) {
      onCreateNew(typed)
      return
    }
    const c = matches[idx]
    if (!c) return
    onAdd(c.contact_id)
    setQuery('')
    setHighlight(0)
    inputRef.current?.focus()
  }

  function onKeyDown(ev) {
    if (ev.key === 'Escape')         { ev.preventDefault(); onClose() }
    else if (ev.key === 'ArrowDown') { ev.preventDefault(); setHighlight(h => Math.min(rowCount - 1, h + 1)) }
    else if (ev.key === 'ArrowUp')   { ev.preventDefault(); setHighlight(h => Math.max(0, h - 1)) }
    else if (ev.key === 'Enter')     { ev.preventDefault(); pick(highlight) }
  }

  const createRow = (
    <div
      onMouseEnter={() => setHighlight(createIdx)}
      onMouseDown={ev => { ev.preventDefault(); pick(createIdx) }}
      style={{
        padding: '7px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
        fontStyle: 'italic', color: 'var(--accent)',
        borderTop: matches.length > 0 ? '1px solid var(--border)' : undefined,
        background: highlight === createIdx ? 'var(--accent-muted)' : 'transparent',
      }}
    >
      + Create "{typed}" as new contact
    </div>
  )

  return (
    <div style={{ position: 'relative', marginTop: 8 }}>
      <input
        ref={inputRef}
        className="input"
        value={query}
        placeholder="Search contacts to add…"
        onChange={e => { setQuery(e.target.value); setHighlight(0) }}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(onClose, 150)}
        style={{ fontSize: 13 }}
      />
      <div style={{
        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 2,
        maxHeight: 6 * 38, overflowY: 'auto',
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
      }}>
        {matches.length === 0 && !showCreate ? (
          <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>No matches</div>
        ) : (
          <>
            {matches.map((c, i) => (
              <div
                key={c.contact_id}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={ev => { ev.preventDefault(); pick(i) }}
                style={{
                  padding: '7px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                  background: highlight === i ? 'var(--accent-muted)' : 'transparent',
                }}
              >
                <span style={{ color: 'var(--text-primary)' }}>{c.name}</span>
                {firmNames(c) && (
                  <span style={{ color: 'var(--text-muted)' }}> · {firmNames(c)}</span>
                )}
              </div>
            ))}
            {showCreate && createRow}
          </>
        )}
      </div>
    </div>
  )
}

// ── Add-firm typeahead (used inside the detail modal for linking firms) ───
// Stays open after each pick so multiple firms can be added in a row.
// onCreateNew(name) is called when the user picks the "+ Create" option.
function AddFirmTypeahead({ firms, onAdd, onClose, onCreateNew }) {
  const [query, setQuery]         = useState('')
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const typed      = query.trim()
  const q          = typed.toLowerCase()
  const matches    = (q ? firms.filter(f => f.name.toLowerCase().includes(q)) : firms).slice(0, 50)
  const hasExact   = q && firms.some(f => f.name.trim().toLowerCase() === q)
  const showCreate = typed.length > 0 && !hasExact
  const createIdx  = matches.length
  const rowCount   = matches.length + (showCreate ? 1 : 0)

  function pick(idx) {
    if (showCreate && idx === createIdx) {
      onCreateNew?.(typed)
      return
    }
    const f = matches[idx]
    if (!f) return
    onAdd(f.firm_id)
    setQuery('')
    setHighlight(0)
    inputRef.current?.focus()
  }

  function onKeyDown(ev) {
    if (ev.key === 'Escape')         { ev.preventDefault(); onClose() }
    else if (ev.key === 'ArrowDown') { ev.preventDefault(); setHighlight(h => Math.min(rowCount - 1, h + 1)) }
    else if (ev.key === 'ArrowUp')   { ev.preventDefault(); setHighlight(h => Math.max(0, h - 1)) }
    else if (ev.key === 'Enter')     { ev.preventDefault(); pick(highlight) }
  }

  return (
    <div style={{ position: 'relative', marginTop: 8 }}>
      <input
        ref={inputRef}
        className="input"
        value={query}
        placeholder="Search firms to link…"
        onChange={e => { setQuery(e.target.value); setHighlight(0) }}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(onClose, 150)}
        style={{ fontSize: 13 }}
      />
      <div style={{
        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 2,
        maxHeight: 7 * 36, overflowY: 'auto',
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
      }}>
        {matches.length === 0 && !showCreate && (
          <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>No matches</div>
        )}
        {matches.map((f, i) => (
          <div
            key={f.firm_id}
            onMouseEnter={() => setHighlight(i)}
            onMouseDown={ev => { ev.preventDefault(); pick(i) }}
            style={{
              padding: '7px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
              background: highlight === i ? 'var(--accent-muted)' : 'transparent',
            }}
          >
            <span style={{ color: 'var(--text-primary)' }}>{f.name}</span>
          </div>
        ))}
        {showCreate && (
          <div
            onMouseEnter={() => setHighlight(createIdx)}
            onMouseDown={ev => { ev.preventDefault(); pick(createIdx) }}
            style={{
              padding: '7px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
              fontStyle: 'italic', color: 'var(--accent)',
              borderTop: matches.length > 0 ? '1px solid var(--border)' : undefined,
              background: highlight === createIdx ? 'var(--accent-muted)' : 'transparent',
            }}
          >+ Create "{typed}" as new firm</div>
        )}
      </div>
    </div>
  )
}

// ── Add-mandate typeahead (used inside the detail modal) ───────────────────
// Stays open after each pick so multiple mandates can be added in a row.
// No "create new" option — mandates are a fixed, curated list.
function AddMandateTypeahead({ mandates, onAdd, onClose }) {
  const [query, setQuery]         = useState('')
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const q       = query.trim().toLowerCase()
  const matches = (q ? mandates.filter(m => m.mandate_name.toLowerCase().includes(q)) : mandates).slice(0, 50)

  function pick(idx) {
    const m = matches[idx]
    if (!m) return
    onAdd(m.mandate_id)
    setQuery('')
    setHighlight(0)
    inputRef.current?.focus()
  }

  function onKeyDown(ev) {
    if (ev.key === 'Escape')         { ev.preventDefault(); onClose() }
    else if (ev.key === 'ArrowDown') { ev.preventDefault(); setHighlight(h => Math.min(matches.length - 1, h + 1)) }
    else if (ev.key === 'ArrowUp')   { ev.preventDefault(); setHighlight(h => Math.max(0, h - 1)) }
    else if (ev.key === 'Enter')     { ev.preventDefault(); pick(highlight) }
  }

  return (
    <div style={{ position: 'relative', marginTop: 8 }}>
      <input
        ref={inputRef}
        className="input"
        value={query}
        placeholder="Search mandates to link…"
        onChange={e => { setQuery(e.target.value); setHighlight(0) }}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(onClose, 150)}
        style={{ fontSize: 13 }}
      />
      <div style={{
        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 2,
        maxHeight: 7 * 36, overflowY: 'auto',
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
      }}>
        {matches.length === 0 ? (
          <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>No matches</div>
        ) : (
          matches.map((m, i) => (
            <div
              key={m.mandate_id}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={ev => { ev.preventDefault(); pick(i) }}
              style={{
                padding: '7px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                background: highlight === i ? 'var(--accent-muted)' : 'transparent',
              }}
            >
              <span style={{ color: 'var(--text-primary)' }}>{m.mandate_name}</span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ── Firm typeahead used inside the Create-Contact mini-modal ───────────────
// Searches existing firms; when no exact match, offers "+ Create firm".
// Calls onPick({ firm_id }) for an existing firm, or onPick({ newName })
// to signal a firm that should be created on submit.
function FirmPicker({ firms, value, onPick }) {
  const [query, setQuery]         = useState(value?.label ?? '')
  const [open, setOpen]           = useState(false)
  const [highlight, setHighlight] = useState(0)

  const typed   = query.trim()
  const q       = typed.toLowerCase()
  const matches = (q ? firms.filter(f => f.name.toLowerCase().includes(q)) : firms).slice(0, 50)
  const hasExact   = q && firms.some(f => f.name.trim().toLowerCase() === q)
  const showCreate = typed.length > 0 && !hasExact
  const createIdx  = matches.length
  const rowCount   = matches.length + (showCreate ? 1 : 0)

  function pick(idx) {
    if (showCreate && idx === createIdx) {
      onPick({ newName: typed, label: typed })
      setQuery(typed); setOpen(false); return
    }
    const f = matches[idx]
    if (!f) return
    onPick({ firm_id: f.firm_id, label: f.name })
    setQuery(f.name); setOpen(false)
  }

  function onKeyDown(ev) {
    if (ev.key === 'Escape')         { ev.preventDefault(); setOpen(false) }
    else if (ev.key === 'ArrowDown') { ev.preventDefault(); setOpen(true); setHighlight(h => Math.min(rowCount - 1, h + 1)) }
    else if (ev.key === 'ArrowUp')   { ev.preventDefault(); setHighlight(h => Math.max(0, h - 1)) }
    else if (ev.key === 'Enter')     { if (open) { ev.preventDefault(); pick(highlight) } }
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="input"
        value={query}
        placeholder="Search firms… (optional)"
        onChange={e => { setQuery(e.target.value); setOpen(true); setHighlight(0); onPick(null) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ fontSize: 13 }}
      />
      {open && (matches.length > 0 || showCreate) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, marginTop: 2,
          maxHeight: 6 * 36, overflowY: 'auto',
          background: 'var(--surface)', border: '1px solid var(--border-light)',
          borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        }}>
          {matches.map((f, i) => (
            <div
              key={f.firm_id}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={ev => { ev.preventDefault(); pick(i) }}
              style={{
                padding: '7px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                background: highlight === i ? 'var(--accent-muted)' : 'transparent',
              }}
            >{f.name}</div>
          ))}
          {showCreate && (
            <div
              onMouseEnter={() => setHighlight(createIdx)}
              onMouseDown={ev => { ev.preventDefault(); pick(createIdx) }}
              style={{
                padding: '7px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                fontStyle: 'italic', color: 'var(--accent)',
                borderTop: matches.length > 0 ? '1px solid var(--border)' : undefined,
                background: highlight === createIdx ? 'var(--accent-muted)' : 'transparent',
              }}
            >+ Create firm "{typed}"</div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Create-Contact mini-modal ─────────────────────────────────────────────
// Creates a contact (and optionally a firm), then links it to the interaction.
function CreateContactMiniModal({ initialName, firms, onCancel, onCreatedAndLinked }) {
  const { contactTypes } = useRoleOptions()
  const [name, setName]         = useState(initialName ?? '')
  const [email, setEmail]       = useState('')
  const [title, setTitle]       = useState('')
  const [types, setTypes]       = useState([])
  const [firmSel, setFirmSel]   = useState(null)   // { firm_id?, newName?, label }
  const [busy, setBusy]         = useState(false)
  const [err, setErr]           = useState(null)
  // Track work already done so a failed LINK can be retried without
  // re-creating the firm/contact.
  const [createdContact, setCreatedContact] = useState(null)
  const [createdFirmId, setCreatedFirmId]   = useState(null)

  function toggleType(t) {
    setTypes(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t])
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!name.trim()) return setErr('Name is required.')
    setBusy(true); setErr(null)
    try {
      // Retry path: contact already created, only the link failed.
      let contact = createdContact

      if (!contact) {
        // 1. Resolve / create firm (only once)
        let firmId = firmSel?.firm_id ?? createdFirmId ?? null
        if (!firmId && firmSel?.newName) {
          const fRes = await api.post('/api/firms', {
            name: firmSel.newName, firm_type: ['LP / Investor'],
          })
          firmId = fRes.data.data.firm_id
          setCreatedFirmId(firmId)
        }

        // 2. Create the contact
        const payload = { name: name.trim() }
        if (email.trim())  payload.email           = [email.trim()]
        if (firmId)        payload.primary_firm_id  = firmId
        if (types.length)  payload.contact_type     = types
        if (title.trim())  payload.title_role       = title.trim()

        const cRes = await api.post('/api/contacts', payload)
        contact = cRes.data.data
        setCreatedContact(contact)
      }

      // 3. Link (parent does optimistic add + closes on success).
      //    If this throws, contact persists; "Retry link" re-runs only step 3.
      await onCreatedAndLinked(contact)
    } catch (e2) {
      setErr(e2.response?.data?.error || e2.message || 'Failed to create contact')
      setBusy(false)
    }
  }

  return (
    <Modal title="Create Contact" onClose={busy ? () => {} : onCancel} width={460} zIndex={1200}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {err && (
          <div style={{
            background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 6, padding: '10px 14px', fontSize: 13, color: 'var(--danger-text)',
          }}>{err}</div>
        )}

        <div className="input-group">
          <label className="input-label">Name *</label>
          <input className="input" value={name} autoFocus
            onChange={e => { setName(e.target.value); setErr(null) }} placeholder="Full name" />
        </div>

        <div className="input-group">
          <label className="input-label">Email</label>
          <input className="input" type="email" value={email}
            onChange={e => setEmail(e.target.value)} placeholder="name@firm.com" />
        </div>

        <div className="input-group">
          <label className="input-label">Firm</label>
          <FirmPicker firms={firms} value={firmSel} onPick={setFirmSel} />
          {firmSel?.newName && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
              Will create new firm "{firmSel.newName}" (LP / Investor)
            </div>
          )}
        </div>

        <div className="input-group">
          <label className="input-label">Contact Type</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {contactTypes.map(t => {
              const sel = types.includes(t)
              return (
                <button key={t} type="button" onClick={() => toggleType(t)}
                  style={{
                    padding: '4px 10px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                    border: `1px solid ${sel ? 'var(--accent)' : 'var(--border-light)'}`,
                    background: sel ? 'var(--accent-muted)' : 'transparent',
                    color: sel ? 'var(--blue-text)' : 'var(--text-secondary)', fontFamily: 'inherit',
                  }}>{t}</button>
              )
            })}
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Title / Role</label>
          <input className="input" value={title}
            onChange={e => setTitle(e.target.value)} placeholder="e.g. Managing Director" />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10,
          paddingTop: 4, borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Working…' : createdContact ? 'Retry link' : 'Create & Link'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Create-Firm mini-modal ────────────────────────────────────────────────
// Creates a new firm then calls onCreatedAndLinked(firm) so the parent can
// link it to the interaction. Retry-safe: once the firm is created, only
// the link is retried on failure.
function CreateFirmMiniModal({ initialName, onCancel, onCreatedAndLinked }) {
  const { firmTypes } = useRoleOptions()
  const [name, setName]       = useState(initialName ?? '')
  const [types, setTypes]     = useState([])
  const [website, setWebsite] = useState('')
  const [hq, setHq]           = useState('')
  const [busy, setBusy]       = useState(false)
  const [err, setErr]         = useState(null)
  const [createdFirm, setCreatedFirm] = useState(null)

  function toggleType(t) {
    setTypes(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t])
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!name.trim())   return setErr('Firm name is required.')
    if (!types.length)  return setErr('Select at least one firm type.')
    setBusy(true); setErr(null)
    try {
      let firm = createdFirm
      if (!firm) {
        const payload = { name: name.trim(), firm_type: types }
        if (website.trim()) payload.website     = website.trim()
        if (hq.trim())      payload.hq_location = hq.trim()
        const res = await api.post('/api/firms', payload)
        firm = res.data.data
        setCreatedFirm(firm)
      }
      await onCreatedAndLinked(firm)
    } catch (e2) {
      setErr(e2.response?.data?.error || e2.message || 'Failed to create firm')
      setBusy(false)
    }
  }

  return (
    <Modal title="Create Firm" onClose={busy ? () => {} : onCancel} width={460} zIndex={1200}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {err && (
          <div style={{
            background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 6, padding: '10px 14px', fontSize: 13, color: 'var(--danger-text)',
          }}>{err}</div>
        )}

        <div className="input-group">
          <label className="input-label">Firm Name *</label>
          <input className="input" value={name} autoFocus
            onChange={e => { setName(e.target.value); setErr(null) }} placeholder="e.g. Blackstone" />
        </div>

        <div className="input-group">
          <label className="input-label">Firm Type *</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {firmTypes.map(t => {
              const sel = types.includes(t)
              return (
                <button key={t} type="button" onClick={() => toggleType(t)}
                  style={{
                    padding: '4px 10px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                    border: `1px solid ${sel ? 'var(--accent)' : 'var(--border-light)'}`,
                    background: sel ? 'var(--accent-muted)' : 'transparent',
                    color: sel ? 'var(--blue-text)' : 'var(--text-secondary)', fontFamily: 'inherit',
                  }}>{t}</button>
              )
            })}
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">Website</label>
          <input className="input" value={website}
            onChange={e => setWebsite(e.target.value)} placeholder="e.g. blackstone.com" />
        </div>

        <div className="input-group">
          <label className="input-label">HQ Location</label>
          <input className="input" value={hq}
            onChange={e => setHq(e.target.value)} placeholder="e.g. New York, NY" />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10,
          paddingTop: 4, borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Working…' : createdFirm ? 'Retry link' : 'Create & Link'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Create-Pipeline-Entry mini-modal (used inside the detail modal) ────────
// Creates an investor_pipeline row for the interaction's mandate, then links
// this interaction to it. Firm is pre-filled when the interaction has
// exactly one linked firm, but always changeable — any existing firm can be
// picked, not just ones already typed "LP / Investor".
function CreatePipelineEntryMiniModal({ mandateId, firms, defaultFirmId, onCancel, onCreated }) {
  const [firmId, setFirmId]       = useState(defaultFirmId ?? '')
  const [contactId, setContactId] = useState('')
  const [stage, setStage]         = useState('Awaiting Client Approval')
  const [busy, setBusy]           = useState(false)
  const [err, setErr]             = useState(null)

  const { data: firmContactsData } = useQuery({
    queryKey: ['contacts', { firm_id: firmId }],
    queryFn: () => api.get('/api/contacts', { params: { firm_id: firmId } }).then(r => r.data.data ?? []),
    enabled: !!firmId,
  })
  const firmContacts = firmContactsData ?? []

  async function handleSubmit(e) {
    e.preventDefault()
    if (!firmId) return setErr('Investor firm is required.')
    setBusy(true); setErr(null)
    try {
      const payload = { stage, investor_firm_id: firmId }
      if (contactId) payload.investor_contact_id = contactId
      const res = await api.post(`/api/mandates/${mandateId}/pipeline`, payload)
      await onCreated(res.data.data)
    } catch (e2) {
      setErr(e2.response?.data?.error || e2.message || 'Failed to create pipeline entry')
      setBusy(false)
    }
  }

  return (
    <Modal title="Create Pipeline Entry" onClose={busy ? () => {} : onCancel} width={440} zIndex={1200}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {err && (
          <div style={{
            background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 6, padding: '10px 14px', fontSize: 13, color: 'var(--danger-text)',
          }}>{err}</div>
        )}

        <div className="input-group">
          <label className="input-label">Investor Firm *</label>
          <EntityField kind="firm" records={firms} value={firmId} allowClear={false}
            createDefaults={{ firm_type: ['LP / Investor'] }}
            onChange={id => { setFirmId(id ?? ''); setContactId(''); setErr(null) }} />
        </div>

        <div className="input-group">
          <label className="input-label">Investor Contact</label>
          {/* The firm's own contacts first, then everyone */}
          <EntityField kind="contact" value={contactId}
            priorityIds={new Set(firmContacts.map(c => c.contact_id))}
            createDefaults={{ contact_type: ['Investor'], primary_firm_id: firmId || null }}
            onChange={id => setContactId(id ?? '')} />
        </div>

        <div className="input-group">
          <label className="input-label">Stage</label>
          <select className="select" value={stage} onChange={e => setStage(e.target.value)}>
            {PIPELINE_STAGES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10,
          paddingTop: 4, borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create & Link'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Action items section (inside the detail modal) ────────────────────────

function shortDate(d) {
  if (!d) return ''
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(d) ? d + 'T00:00:00' : d
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function ActionItemsSection({ interactionId, linkedMandates = [] }) {
  const qc = useQueryClient()
  const [note, setNote]       = useState('')
  const [assignTo, setAssign] = useState('')
  const [due, setDue]         = useState('')
  // With one mandate tag the server fills mandate_id itself; with several the
  // user picks which mandate the item belongs to.
  const [mandateId, setMandateId] = useState('')

  const key = ['action-items', interactionId]

  const { data: items = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.get(`/api/interactions/${interactionId}/action-items`).then(r => r.data.data ?? []),
  })
  const { data: users = [] } = useQuery({
    queryKey: ['users', 'staff'],
    queryFn: () => api.get('/api/users', { params: { staff: 'true' } }).then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })

  // Refresh every action-item view (Dashboard, mandate pages), not just this one
  function refreshUpcoming() { qc.invalidateQueries({ queryKey: ['action-items'] }) }

  const createMutation = useMutation({
    mutationFn: (payload) =>
      api.post(`/api/interactions/${interactionId}/action-items`, payload).then(r => r.data),
    onSuccess: () => {
      setNote(''); setAssign(''); setDue(''); setMandateId('')
      qc.invalidateQueries({ queryKey: key })
      refreshUpcoming()
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, completed }) =>
      api.patch(`/api/action-items/${id}`, { completed }).then(r => r.data),
    onMutate: async ({ id, completed }) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData(key)
      qc.setQueryData(key, old => (old ?? []).map(a =>
        a.action_item_id === id
          ? { ...a, completed, completed_at: completed ? new Date().toISOString() : null }
          : a))
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev) },
    onSettled: () => { qc.invalidateQueries({ queryKey: key }); refreshUpcoming() },
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/action-items/${id}`).then(r => r.data),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData(key)
      qc.setQueryData(key, old => (old ?? []).filter(a => a.action_item_id !== id))
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev) },
    onSettled: () => { qc.invalidateQueries({ queryKey: key }); refreshUpcoming() },
  })

  function submit() {
    const trimmed = note.trim()
    if (!trimmed) return
    createMutation.mutate({
      note: trimmed,
      assigned_to: assignTo || null,
      due_date:    due || null,
      ...(mandateId ? { mandate_id: mandateId } : {}),
    })
  }

  return (
    <>
      <div className="divider" />
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
          Action Items
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No action items</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {items.map(a => {
              const overdue = !a.completed && isOverdue(a.due_date)
              return (
                <div key={a.action_item_id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 4px', borderBottom: '1px solid var(--border)',
                }}>
                  <input
                    type="checkbox"
                    checked={!!a.completed}
                    onChange={() => toggleMutation.mutate({ id: a.action_item_id, completed: !a.completed })}
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }}
                  />
                  <span style={{
                    flex: 1, fontSize: 13,
                    textDecoration: a.completed ? 'line-through' : 'none',
                    color: a.completed ? 'var(--text-muted)' : 'var(--text-primary)',
                  }}>{a.note}</span>
                  {a.mandate?.mandate_name && linkedMandates.length > 1 && (
                    <span style={{ fontSize: 10, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {a.mandate.mandate_name}
                    </span>
                  )}
                  {a.assigned_user?.name && (
                    <span style={{
                      fontSize: 10, padding: '2px 7px', borderRadius: 10,
                      background: 'var(--surface-raised)', border: '1px solid var(--border)',
                      color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                    }}>{a.assigned_user.name}</span>
                  )}
                  {a.due_date && (
                    <span style={{
                      fontSize: 11, whiteSpace: 'nowrap',
                      color: overdue ? 'var(--danger)' : 'var(--text-secondary)',
                      fontWeight: overdue ? 600 : 400,
                    }}>{shortDate(a.due_date)}</span>
                  )}
                  <button
                    onClick={() => deleteMutation.mutate(a.action_item_id)}
                    title="Delete action item"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                      color: 'var(--text-muted)', fontSize: 14, lineHeight: 1, flexShrink: 0,
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                  >×</button>
                </div>
              )
            })}
          </div>
        )}

        {/* Inline add form (always visible) */}
        <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
          <input
            className="input"
            value={note}
            placeholder="Add action item…"
            onChange={e => setNote(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
            style={{ flex: 1, fontSize: 13 }}
          />
          <select className="select" value={assignTo} onChange={e => setAssign(e.target.value)}
            style={{ fontSize: 12, maxWidth: 120 }}>
            <option value="">Assign…</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {linkedMandates.length > 1 && (
            <select className="select" value={mandateId} onChange={e => setMandateId(e.target.value)}
              style={{ fontSize: 12, maxWidth: 140 }} aria-label="Mandate">
              <option value="">Mandate…</option>
              {linkedMandates.map(m => <option key={m.mandate_id} value={m.mandate_id}>{m.mandate_name}</option>)}
            </select>
          )}
          <input type="date" className="input" value={due} onChange={e => setDue(e.target.value)}
            style={{ fontSize: 12, maxWidth: 140 }} />
          <button
            className="btn btn-primary btn-sm"
            onClick={submit}
            disabled={createMutation.isPending || !note.trim()}
            style={{ flexShrink: 0 }}
          >
            {createMutation.isPending ? 'Adding…' : '+ Add'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Interaction detail modal ──────────────────────────────────────────────

function InteractionDetailModal({ interaction, onClose }) {
  const qc = useQueryClient()
  const [adding, setAdding]               = useState(false)
  const [addingFirm, setAddingFirm]       = useState(false)
  const [addingMandate, setAddingMandate] = useState(false)
  const [createName, setCreateName]       = useState(null)  // non-null → mini-modal open
  const [flash, setFlash]                 = useState(null)   // brief success message
  const [aiSuggesting, setAiSuggesting]         = useState(false)
  const [aiSuggestion, setAiSuggestion]         = useState(null)
  const [aiFirmSuggesting, setAiFirmSuggesting] = useState(false)
  const [aiFirmSuggestion, setAiFirmSuggestion] = useState(null)
  const [domainDismissed, setDomainDismissed]   = useState(false)
  const [dismissedFirmIds, setDismissedFirmIds] = useState(() => new Set())
  const [createFirmName, setCreateFirmName]     = useState(null)  // non-null → firm mini-modal open
  const [creatingPipelineEntry, setCreatingPipelineEntry] = useState(false)

  // Fetch full detail to get embedded contacts
  const { data: detailData, isLoading } = useQuery({
    queryKey: ['interaction', interaction.interaction_id],
    queryFn: () => api.get(`/api/interactions/${interaction.interaction_id}`).then(r => r.data.data),
  })

  // Mandates for the inline editable dropdown
  const { data: mandatesData } = useQuery({
    queryKey: ['mandates'],
    queryFn: () => api.get('/api/mandates').then(r => r.data.data ?? []),
  })
  const mandates = mandatesData ?? []

  // RGA users — resolves mailbox_owner (a connected-account email) to a
  // display name for the "synced via" indicator below.
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/api/users').then(r => r.data.data ?? []),
  })
  const users = usersData ?? []

  // All contacts for the add-contact typeahead (reuses the shared cache key)
  const { data: allContactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const allContacts = allContactsData ?? []

  // Firms for the create-contact mini-modal's firm picker
  const { data: firmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const firms = firmsData ?? []

  const detail = detailData ?? interaction // fall back to list data while loading
  const iid = interaction.interaction_id
  const linkedMandates   = (detail.interaction_mandates ?? []).map(im => im.mandate).filter(Boolean)
  const linkedMandateIds = new Set(linkedMandates.map(m => m.mandate_id))
  const addableMandates  = mandates.filter(m => !linkedMandateIds.has(m.mandate_id))
  // The manual pipeline-entry picker/creator below is inherently "pick one
  // mandate's pipeline" — with multi-mandate tagging an interaction can now
  // have several, so this uses the first linked mandate as a reasonable
  // default rather than reworking that picker into its own mandate-selector.
  const mandateId = linkedMandates[0]?.mandate_id ?? ''

  // Pipeline entries for the (first) linked mandate — populates the manual picker.
  const { data: pipelineData } = useQuery({
    queryKey: ['pipeline', mandateId],
    queryFn: () => api.get(`/api/mandates/${mandateId}/pipeline`).then(r => r.data.data ?? []),
    enabled: !!mandateId,
  })
  const pipelineEntries = pipelineData ?? []

  // Link a mandate — optimistic add of the chip. Mirrors linkFirmMutation.
  // The API also propagates it to other emails in the same Gmail thread and
  // re-resolves pipeline_id; surface how many thread rows were updated.
  const linkMandateMutation = useMutation({
    mutationFn: (mandateId) =>
      api.post(`/api/interactions/${iid}/mandates/${mandateId}`).then(r => r.data),
    onMutate: async (mandateId) => {
      await qc.cancelQueries({ queryKey: ['interaction', iid] })
      const prev = qc.getQueryData(['interaction', iid])
      const m = mandates.find(x => x.mandate_id === mandateId)
      if (m) {
        qc.setQueryData(['interaction', iid], old => old ? {
          ...old,
          interaction_mandates: [
            ...(old.interaction_mandates ?? []),
            { mandate: { mandate_id: m.mandate_id, mandate_name: m.mandate_name } },
          ],
        } : old)
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['interaction', iid], ctx.prev) },
    onSuccess: (envelope) => {
      const prop = envelope?.data?.thread_propagation ?? envelope?.thread_propagation ?? {}
      showThreadFlash(prop)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['interaction', iid] })
      qc.invalidateQueries({ queryKey: ['interactions'] })
    },
  })

  // Unlink a mandate — optimistic removal of the chip.
  const unlinkMandateMutation = useMutation({
    mutationFn: (mandateId) =>
      api.delete(`/api/interactions/${iid}/mandates/${mandateId}`).then(r => r.data),
    onMutate: async (mandateId) => {
      await qc.cancelQueries({ queryKey: ['interaction', iid] })
      const prev = qc.getQueryData(['interaction', iid])
      qc.setQueryData(['interaction', iid], old => old ? {
        ...old,
        interaction_mandates: (old.interaction_mandates ?? [])
          .filter(im => im.mandate?.mandate_id !== mandateId),
      } : old)
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['interaction', iid], ctx.prev) },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['interaction', iid] })
      qc.invalidateQueries({ queryKey: ['interactions'] })
    },
  })

  // PATCH pipeline_id (manual override of the auto-resolved/ambiguous entry)
  const pipelineMutation = useMutation({
    mutationFn: (pipeline_id) =>
      api.patch(`/api/interactions/${iid}`, { pipeline_id: pipeline_id || null }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interaction', iid] })
      qc.invalidateQueries({ queryKey: ['interactions'] })
    },
  })

  // PATCH the stage on the LINKED pipeline entry itself (not the interaction) —
  // lets the stage move forward without leaving the interaction to find the
  // entry on the mandate's BD Pipeline board.
  const stageMutation = useMutation({
    mutationFn: (stage) =>
      api.patch(`/api/pipeline/${detail.pipeline?.pipeline_id}`, { stage }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interaction', iid] })
      qc.invalidateQueries({ queryKey: ['pipeline', mandateId] })
    },
  })

  // PATCH follow_up_due (empty string → null to clear)
  const followUpMutation = useMutation({
    mutationFn: (val) =>
      api.patch(`/api/interactions/${iid}`, { follow_up_due: val || null }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interaction', iid] })
      qc.invalidateQueries({ queryKey: ['interactions'] })
    },
  })

  // Link a contact — optimistic add of the chip
  const linkMutation = useMutation({
    mutationFn: (contactId) =>
      api.post(`/api/interactions/${iid}/contacts/${contactId}`).then(r => r.data),
    onMutate: async (contactId) => {
      await qc.cancelQueries({ queryKey: ['interaction', iid] })
      const prev = qc.getQueryData(['interaction', iid])
      const c = allContacts.find(x => x.contact_id === contactId)
      if (c) {
        qc.setQueryData(['interaction', iid], old => old ? {
          ...old,
          interaction_contacts: [
            ...(old.interaction_contacts ?? []),
            { contact: { contact_id: c.contact_id, name: c.name, title_role: c.title_role, email: c.email } },
          ],
        } : old)
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['interaction', iid], ctx.prev) },
    onSuccess: (envelope) => {
      const body = envelope?.data ?? envelope ?? {}
      showThreadFlash(body.thread_propagation ?? {})
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['interaction', iid] })
      qc.invalidateQueries({ queryKey: ['interactions'] })
    },
  })

  // Unlink a contact — optimistic removal of the chip
  const unlinkMutation = useMutation({
    mutationFn: (contactId) =>
      api.delete(`/api/interactions/${iid}/contacts/${contactId}`).then(r => r.data),
    onMutate: async (contactId) => {
      await qc.cancelQueries({ queryKey: ['interaction', iid] })
      const prev = qc.getQueryData(['interaction', iid])
      qc.setQueryData(['interaction', iid], old => old ? {
        ...old,
        interaction_contacts: (old.interaction_contacts ?? [])
          .filter(ic => ic.contact?.contact_id !== contactId),
      } : old)
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['interaction', iid], ctx.prev) },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['interaction', iid] })
      qc.invalidateQueries({ queryKey: ['interactions'] })
    },
  })

  // Link a firm
  const linkFirmMutation = useMutation({
    mutationFn: (firmId) =>
      api.post(`/api/interactions/${iid}/firms/${firmId}`).then(r => r.data),
    onMutate: async (firmId) => {
      await qc.cancelQueries({ queryKey: ['interaction', iid] })
      const prev = qc.getQueryData(['interaction', iid])
      const f = firms.find(x => x.firm_id === firmId)
      if (f) {
        qc.setQueryData(['interaction', iid], old => old ? {
          ...old,
          interaction_firms: [
            ...(old.interaction_firms ?? []),
            { firm: { firm_id: f.firm_id, name: f.name } },
          ],
        } : old)
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['interaction', iid], ctx.prev) },
    onSuccess: (envelope) => {
      const prop = envelope?.data?.thread_propagation ?? envelope?.thread_propagation ?? {}
      showThreadFlash(prop)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['interaction', iid] }),
  })

  // Unlink a firm
  const unlinkFirmMutation = useMutation({
    mutationFn: (firmId) =>
      api.delete(`/api/interactions/${iid}/firms/${firmId}`).then(r => r.data),
    onMutate: async (firmId) => {
      await qc.cancelQueries({ queryKey: ['interaction', iid] })
      const prev = qc.getQueryData(['interaction', iid])
      qc.setQueryData(['interaction', iid], old => old ? {
        ...old,
        interaction_firms: (old.interaction_firms ?? [])
          .filter(if_ => if_.firm?.firm_id !== firmId),
      } : old)
      return { prev }
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['interaction', iid], ctx.prev) },
    onSettled: () => qc.invalidateQueries({ queryKey: ['interaction', iid] }),
  })

  async function handleSuggestMandate() {
    setAiSuggesting(true)
    setAiSuggestion(null)
    try {
      const r = await api.post(`/api/interactions/${iid}/suggest-mandate`)
      setAiSuggestion(r.data.data ?? r.data)
    } catch {
      setAiSuggestion({ error: 'AI suggestion unavailable' })
    } finally {
      setAiSuggesting(false)
    }
  }

  async function handleAcceptSuggestion() {
    if (!aiSuggestion?.mandate_id) return
    linkMandateMutation.mutate(aiSuggestion.mandate_id)
    setAiSuggestion(null)
  }

  async function handleSuggestFirm() {
    setAiFirmSuggesting(true)
    setAiFirmSuggestion(null)
    try {
      const r = await api.post(`/api/interactions/${iid}/suggest-firm`)
      setAiFirmSuggestion(r.data.data ?? r.data)
    } catch {
      setAiFirmSuggestion({ error: 'AI suggestion unavailable' })
    } finally {
      setAiFirmSuggesting(false)
    }
  }

  async function handleAcceptFirmSuggestion() {
    if (!aiFirmSuggestion?.firm_id) return
    linkFirmMutation.mutate(aiFirmSuggestion.firm_id)
    setAiFirmSuggestion(null)
  }

  const linkedContacts  = (detail.interaction_contacts ?? []).map(ic => ic.contact).filter(Boolean)
  const linkedIds       = new Set(linkedContacts.map(c => c.contact_id))
  const addableContacts = allContacts.filter(c => !linkedIds.has(c.contact_id))

  const linkedFirms     = (detail.interaction_firms ?? []).map(if_ => if_.firm).filter(Boolean)
  const linkedFirmIds   = new Set(linkedFirms.map(f => f.firm_id))
  const addableFirms    = firms.filter(f => !linkedFirmIds.has(f.firm_id))

  // Candidate firms from linked contacts' primary_firm_id / contact_firms —
  // computed server-side at read time so they show up regardless of how the
  // contact got linked (manual add, Gmail/Calendar sync, thread propagation).
  const suggestedFirms = (detail.suggested_firms ?? [])
    .filter(s => !linkedFirmIds.has(s.firm_id) && !dismissedFirmIds.has(s.firm_id))

  // Build a flash message from a thread_propagation object
  // { mandate: N, firms: N, contacts: N } or { firms: N } or { contacts: N }
  function showThreadFlash(prop = {}) {
    const parts = []
    if ((prop.mandate  ?? 0) > 0) parts.push(`mandate to ${prop.mandate}`)
    if ((prop.firms    ?? 0) > 0) parts.push(`firm${prop.firms    > 1 ? 's' : ''} to ${prop.firms}`)
    if ((prop.contacts ?? 0) > 0) parts.push(`contact${prop.contacts > 1 ? 's' : ''} to ${prop.contacts}`)
    if (!parts.length) return
    const total = Math.max(prop.mandate ?? 0, prop.firms ?? 0, prop.contacts ?? 0)
    const label = parts.join(', ')
    showFlash(`Applied ${label} other email${total !== 1 ? 's' : ''} in this thread`)
  }

  function showFlash(msg) {
    setFlash(msg)
    setTimeout(() => setFlash(null), 3000)
  }

  // Link an already-created contact: optimistic chip, rollback + rethrow on
  // failure (so the mini-modal can show the error and offer a link retry).
  async function linkCreatedContact(contact) {
    qc.setQueryData(['interaction', iid], old => old ? {
      ...old,
      interaction_contacts: [
        ...(old.interaction_contacts ?? []),
        { contact: { contact_id: contact.contact_id, name: contact.name, title_role: contact.title_role, email: contact.email } },
      ],
    } : old)
    try {
      await api.post(`/api/interactions/${iid}/contacts/${contact.contact_id}`)
    } catch (e) {
      qc.setQueryData(['interaction', iid], old => old ? {
        ...old,
        interaction_contacts: (old.interaction_contacts ?? [])
          .filter(ic => ic.contact?.contact_id !== contact.contact_id),
      } : old)
      throw e
    }
    qc.invalidateQueries({ queryKey: ['interaction', iid] })
    qc.invalidateQueries({ queryKey: ['interactions'] })
    qc.invalidateQueries({ queryKey: ['contacts'] })   // new contact now exists
    setCreateName(null)
    showFlash('Contact created and linked')
  }

  // Create-pipeline-entry mini-modal submitted: link this interaction to the
  // entry just created, then close and refresh.
  async function handlePipelineEntryCreated(entry) {
    await api.patch(`/api/interactions/${iid}`, { pipeline_id: entry.pipeline_id })
    qc.invalidateQueries({ queryKey: ['pipeline', mandateId] })
    qc.invalidateQueries({ queryKey: ['interaction', iid] })
    qc.invalidateQueries({ queryKey: ['interactions'] })
    setCreatingPipelineEntry(false)
    showFlash('Pipeline entry created and linked')
  }

  return (
    <Modal title={detail.subject_title || `${detail.type} interaction`} onClose={onClose} width={560}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Success flash */}
        {flash && (
          <div style={{
            background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: 6, padding: '8px 14px', fontSize: 13, color: 'var(--success-bright)',
          }}>✓ {flash}</div>
        )}

        {/* Badges row */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span className={`badge ${TYPE_BADGE[detail.type] ?? 'badge-gray'}`}>{detail.type}</span>
          {detail.direction && <span className={`badge ${DIR_BADGE[detail.direction] ?? 'badge-gray'}`}>{detail.direction}</span>}
          <span className={`badge ${SOURCE_BADGE[detail.source] ?? 'badge-gray'}`}>{detail.source}</span>
          {detail.mailbox_owner && (
            <span className="badge badge-gray" title={`Synced from ${detail.mailbox_owner}`}>
              Inbox: {users.find(u => u.email === detail.mailbox_owner)?.name ?? detail.mailbox_owner}
            </span>
          )}
        </div>

        {(detail.sender_email || detail.to_emails?.length > 0 || detail.cc_emails?.length > 0) && (
          <div style={{
            fontSize: 12, color: 'var(--text-secondary)', marginTop: -8,
            display: 'flex', flexDirection: 'column', gap: 2,
          }}>
            {detail.sender_email && (
              <div>From: <span style={{ color: 'var(--text-primary)' }}>{detail.sender_email}</span></div>
            )}
            {detail.to_emails?.length > 0 && (
              <div>To: <span style={{ color: 'var(--text-primary)' }}>{detail.to_emails.join(', ')}</span></div>
            )}
            {detail.cc_emails?.length > 0 && (
              <div>Cc: <span style={{ color: 'var(--text-primary)' }}>{detail.cc_emails.join(', ')}</span></div>
            )}
          </div>
        )}

        {/* Detail grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          <Field label="Date">{formatDateTime(detail.date)}</Field>
          <Field label="Follow-up Due">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="date"
                className="input"
                value={(detail.follow_up_due ?? '').slice(0, 10)}
                disabled={followUpMutation.isPending}
                onChange={e => followUpMutation.mutate(e.target.value)}
                style={{ fontSize: 13, padding: '4px 8px', maxWidth: 170 }}
              />
              {detail.follow_up_due && (
                <button
                  type="button"
                  onClick={() => followUpMutation.mutate('')}
                  disabled={followUpMutation.isPending}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontSize: 12, color: 'var(--text-muted)', textDecoration: 'underline',
                  }}
                >× Clear</button>
              )}
            </div>
            {detail.follow_up_due && isOverdue(detail.follow_up_due) && (
              <div style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 600, marginTop: 4 }}>
                Overdue
              </div>
            )}
            {followUpMutation.isError && (
              <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
                {followUpMutation.error?.response?.data?.error || 'Failed to update follow-up date'}
              </div>
            )}
          </Field>
        </div>

        {/* Body / Notes */}
        {detail.body_notes && (
          <>
            <div className="divider" />
            <Field label="Notes / Body">
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                {detail.body_notes}
              </div>
            </Field>
          </>
        )}

        {/* Linked mandates — editable, multi-select */}
        <div className="divider" />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
              Linked Mandates
            </div>
            {linkedMandates.length === 0 && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleSuggestMandate}
                disabled={aiSuggesting}
                title="Ask AI to suggest a mandate"
                style={{ whiteSpace: 'nowrap', fontSize: 12 }}
              >
                {aiSuggesting ? 'Thinking…' : '✨ Suggest'}
              </button>
            )}
          </div>

          {linkedMandates.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No mandates linked</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {linkedMandates.map(m => (
                <span key={m.mandate_id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 6px 4px 10px', borderRadius: 14, fontSize: 12,
                  background: 'var(--surface-raised)', border: '1px solid var(--border)',
                }}>
                  {m.mandate_name}
                  <button
                    onClick={() => unlinkMandateMutation.mutate(m.mandate_id)}
                    title="Remove mandate"
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 16, height: 16, borderRadius: '50%', border: 'none',
                      background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer',
                      fontSize: 13, lineHeight: 1, padding: 0,
                    }}
                    onMouseEnter={ev => { ev.currentTarget.style.background = 'var(--danger-muted)'; ev.currentTarget.style.color = 'var(--danger)' }}
                    onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent'; ev.currentTarget.style.color = 'var(--text-muted)' }}
                  >×</button>
                </span>
              ))}
            </div>
          )}

          {/* AI Suggestion banner */}
          {aiSuggestion && (
            <div style={{
              marginTop: 8, padding: '10px 12px', borderRadius: 8,
              background: 'var(--surface-raised)', border: '1px solid var(--accent)',
              fontSize: 13,
            }}>
              {aiSuggestion.error ? (
                <div>
                  <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>{aiSuggestion.error}</div>
                  {aiSuggestion.detail && (
                    <div style={{ fontSize: 11, color: 'var(--danger)', fontFamily: 'monospace', marginBottom: 6 }}>{aiSuggestion.detail}</div>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => setAiSuggestion(null)}>Dismiss</button>
                </div>
              ) : aiSuggestion.mandate_id ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>✨ AI Suggestion</span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
                      padding: '1px 5px', borderRadius: 4,
                      background: aiSuggestion.confidence === 'high' ? 'var(--success-muted)' : aiSuggestion.confidence === 'medium' ? 'rgba(255,180,0,0.15)' : 'var(--surface)',
                      color: aiSuggestion.confidence === 'high' ? 'var(--success)' : aiSuggestion.confidence === 'medium' ? '#e0a800' : 'var(--text-muted)',
                    }}>
                      {aiSuggestion.confidence} confidence
                    </span>
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>{aiSuggestion.mandate_name}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8, fontStyle: 'italic' }}>"{aiSuggestion.reason}"</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={handleAcceptSuggestion}>Accept</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setAiSuggestion(null)}>Dismiss</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ color: 'var(--text-muted)', marginBottom: 6, fontSize: 12 }}>No clear mandate match found.</div>
                  <button className="btn btn-secondary btn-sm" onClick={() => setAiSuggestion(null)}>Dismiss</button>
                </>
              )}
            </div>
          )}

          {linkMandateMutation.isError && (
            <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
              {linkMandateMutation.error?.response?.data?.error || 'Failed to link mandate'}
            </div>
          )}

          {addingMandate ? (
            <AddMandateTypeahead
              mandates={addableMandates}
              onAdd={id => linkMandateMutation.mutate(id)}
              onClose={() => setAddingMandate(false)}
            />
          ) : (
            <button
              className="btn btn-secondary btn-sm"
              style={{ marginTop: 8 }}
              onClick={() => setAddingMandate(true)}
            >
              + Add mandate
            </button>
          )}
        </div>

        {/* Linked contacts — editable */}
        <div className="divider" />
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
            Linked Contacts
          </div>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
              <div className="spinner" />
            </div>
          ) : (
            <>
              {linkedContacts.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No contacts linked</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {linkedContacts.map(c => (
                    <span key={c.contact_id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 6px 4px 10px', borderRadius: 14, fontSize: 12,
                      background: 'var(--surface-raised)', border: '1px solid var(--border)',
                    }}>
                      {c.name}
                      <button
                        onClick={() => unlinkMutation.mutate(c.contact_id)}
                        title="Remove contact"
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 16, height: 16, borderRadius: '50%', border: 'none',
                          background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer',
                          fontSize: 13, lineHeight: 1, padding: 0,
                        }}
                        onMouseEnter={ev => { ev.currentTarget.style.background = 'var(--danger-muted)'; ev.currentTarget.style.color = 'var(--danger)' }}
                        onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent'; ev.currentTarget.style.color = 'var(--text-muted)' }}
                      >×</button>
                    </span>
                  ))}
                </div>
              )}

              {adding ? (
                <AddContactTypeahead
                  contacts={addableContacts}
                  onAdd={id => linkMutation.mutate(id)}
                  onCreateNew={typed => { setAdding(false); setCreateName(typed) }}
                  onClose={() => setAdding(false)}
                />
              ) : (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: 8 }}
                  onClick={() => setAdding(true)}
                >
                  + Add contact
                </button>
              )}
            </>
          )}
        </div>

        {/* Linked Firms */}
        <div className="divider" />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
              Linked Firms
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleSuggestFirm}
              disabled={aiFirmSuggesting}
              title="Ask AI to suggest a firm"
              style={{ fontSize: 11 }}
            >
              {aiFirmSuggesting ? 'Thinking…' : '✨ Suggest'}
            </button>
          </div>

          {/* AI Firm Suggestion banner */}
          {aiFirmSuggestion && (
            <div style={{
              marginBottom: 10, padding: '10px 12px', borderRadius: 8,
              background: 'var(--surface-raised)', border: '1px solid var(--accent)',
              fontSize: 13,
            }}>
              {aiFirmSuggestion.error ? (
                <div>
                  <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>{aiFirmSuggestion.error}</div>
                  {aiFirmSuggestion.detail && (
                    <div style={{ fontSize: 11, color: 'var(--danger)', fontFamily: 'monospace', marginBottom: 6 }}>{aiFirmSuggestion.detail}</div>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => setAiFirmSuggestion(null)}>Dismiss</button>
                </div>
              ) : aiFirmSuggestion.firm_id ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>✨ AI Suggestion</span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
                      padding: '1px 5px', borderRadius: 4,
                      background: aiFirmSuggestion.confidence === 'high' ? 'var(--success-muted)' : aiFirmSuggestion.confidence === 'medium' ? 'rgba(255,180,0,0.15)' : 'var(--surface)',
                      color: aiFirmSuggestion.confidence === 'high' ? 'var(--success)' : aiFirmSuggestion.confidence === 'medium' ? '#e0a800' : 'var(--text-muted)',
                    }}>
                      {aiFirmSuggestion.confidence} confidence
                    </span>
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>{aiFirmSuggestion.firm_name}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8, fontStyle: 'italic' }}>"{aiFirmSuggestion.reason}"</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-primary btn-sm" onClick={handleAcceptFirmSuggestion}>Accept</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setAiFirmSuggestion(null)}>Dismiss</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ color: 'var(--text-muted)', marginBottom: 6, fontSize: 12 }}>No clear firm match found.</div>
                  <button className="btn btn-secondary btn-sm" onClick={() => setAiFirmSuggestion(null)}>Dismiss</button>
                </>
              )}
            </div>
          )}

          {/* Suggested firms — from linked contacts' primary firm / other firms.
              Each candidate needs an explicit click to attach; nothing here is
              ever inserted automatically. */}
          {suggestedFirms.length > 0 && (
            <div style={{
              marginBottom: 10, padding: '10px 12px', borderRadius: 8,
              background: 'var(--surface-raised)', border: '1px solid var(--border)',
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
                Suggested from linked contacts:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {suggestedFirms.map(s => (
                  <div key={s.firm_id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, flex: 1 }}>
                      <strong style={{ color: 'var(--text-primary)' }}>{s.name}</strong>
                      <span style={{ color: 'var(--text-muted)' }}>
                        {' '}— via {s.via.map(v => v.contact_name).filter(Boolean).join(', ')}
                      </span>
                    </span>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: 11 }}
                      onClick={() => linkFirmMutation.mutate(s.firm_id)}
                    >
                      + Add {s.name}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: 11 }}
                      onClick={() => setDismissedFirmIds(prev => new Set(prev).add(s.firm_id))}
                    >
                      Dismiss
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
              <div className="spinner" />
            </div>
          ) : (
            <>
              {linkedFirms.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No firms linked</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {linkedFirms.map(f => (
                    <span key={f.firm_id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 6px 4px 10px', borderRadius: 14, fontSize: 12,
                      background: 'var(--surface-raised)', border: '1px solid var(--border)',
                    }}>
                      {f.name}
                      <button
                        onClick={() => unlinkFirmMutation.mutate(f.firm_id)}
                        title="Remove firm"
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 16, height: 16, borderRadius: '50%', border: 'none',
                          background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer',
                          fontSize: 13, lineHeight: 1, padding: 0,
                        }}
                        onMouseEnter={ev => { ev.currentTarget.style.background = 'var(--danger-muted)'; ev.currentTarget.style.color = 'var(--danger)' }}
                        onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent'; ev.currentTarget.style.color = 'var(--text-muted)' }}
                      >×</button>
                    </span>
                  ))}
                </div>
              )}

              {addingFirm ? (
                <AddFirmTypeahead
                  firms={addableFirms}
                  onAdd={id => linkFirmMutation.mutate(id)}
                  onClose={() => setAddingFirm(false)}
                  onCreateNew={name => { setAddingFirm(false); setCreateFirmName(name) }}
                />
              ) : (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: 8 }}
                  onClick={() => setAddingFirm(true)}
                >
                  + Add firm
                </button>
              )}

              {/* Sender domain suggestion — show when: gmail sync, no firms linked yet,
                  sender domain isn't personal, and user hasn't dismissed */}
              {(() => {
                if (domainDismissed) return null
                if (linkedFirms.length > 0) return null
                if (detail.source !== 'Gmail Sync') return null
                const sender = detail.sender_email
                if (!sender) return null
                const domain = sender.split('@')[1]?.toLowerCase()
                if (!domain) return null
                const PERSONAL = new Set(['gmail.com','yahoo.com','hotmail.com','outlook.com','icloud.com','me.com','aol.com','mac.com','live.com','msn.com','protonmail.com','rgagroup.net'])
                if (PERSONAL.has(domain)) return null

                // Does a firm already exist with a matching website domain?
                const matchedFirm = firms.find(f => {
                  if (!f.website) return false
                  try {
                    const url = f.website.startsWith('http') ? f.website : 'https://' + f.website
                    const firmDomain = new URL(url).hostname.replace(/^www\./, '')
                    return firmDomain === domain || domain.endsWith('.' + firmDomain) || firmDomain.endsWith('.' + domain)
                  } catch { return false }
                })

                const label = matchedFirm ? matchedFirm.name : domain

                return (
                  <div style={{
                    marginTop: 8, padding: '8px 10px', borderRadius: 6, fontSize: 12,
                    background: 'var(--surface-raised)', border: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                  }}>
                    <span style={{ color: 'var(--text-secondary)', flex: 1 }}>
                      Sender domain: <strong style={{ color: 'var(--text-primary)' }}>{domain}</strong>
                      {matchedFirm ? ' — matches firm in CRM' : ''}
                    </span>
                    {matchedFirm && (
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ fontSize: 11 }}
                        onClick={() => { linkFirmMutation.mutate(matchedFirm.firm_id); setDomainDismissed(true) }}
                      >
                        Add {label}
                      </button>
                    )}
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: 11 }}
                      onClick={() => setDomainDismissed(true)}
                    >
                      Ignore
                    </button>
                  </div>
                )
              })()}
            </>
          )}
        </div>

        {/* Linked Pipeline Entry */}
        <div className="divider" />
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
            Linked Pipeline Entry
          </div>
          {!mandateId ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Assign a mandate to link a pipeline entry
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 6 }}>
                <select
                  className="select"
                  value={detail.pipeline?.pipeline_id ?? ''}
                  disabled={pipelineMutation.isPending}
                  onChange={e => pipelineMutation.mutate(e.target.value)}
                  style={{ fontSize: 13, padding: '4px 8px', flex: 1 }}
                >
                  <option value="">
                    {pipelineEntries.length === 0 ? 'No pipeline entries for this mandate' : 'None'}
                  </option>
                  {pipelineEntries.map(p => (
                    <option key={p.pipeline_id} value={p.pipeline_id}>
                      {(p.investor_firm?.name ?? 'Unknown firm')} — {p.stage}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ whiteSpace: 'nowrap', fontSize: 12 }}
                  onClick={() => setCreatingPipelineEntry(true)}
                >
                  + New
                </button>
              </div>
              {detail.pipeline?.pipeline_id && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Stage</span>
                  <select
                    className="select"
                    value={detail.pipeline.stage}
                    disabled={stageMutation.isPending}
                    onChange={e => stageMutation.mutate(e.target.value)}
                    style={{ fontSize: 13, padding: '4px 8px', flex: 1 }}
                  >
                    {PIPELINE_STAGES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              )}
              {stageMutation.isError && (
                <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
                  {stageMutation.error?.response?.data?.error || 'Failed to update stage'}
                </div>
              )}
              {!detail.pipeline?.pipeline_id && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  {pipelineEntries.length === 0
                    ? 'This mandate has no pipeline entries yet.'
                    : 'Unresolved — either no pipeline entry matches the linked firm(s) yet, or more than one does. Pick the right one above, or create a new entry.'}
                </div>
              )}
              {pipelineMutation.isError && (
                <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
                  {pipelineMutation.error?.response?.data?.error || 'Failed to update pipeline entry'}
                </div>
              )}
            </>
          )}
        </div>

        {/* Action Items */}
        <ActionItemsSection interactionId={iid} linkedMandates={linkedMandates} />

      </div>

      {/* Inline create-contact mini-modal (stacks above this modal) */}
      {createName !== null && (
        <CreateContactMiniModal
          initialName={createName}
          firms={firms}
          onCancel={() => setCreateName(null)}
          onCreatedAndLinked={linkCreatedContact}
        />
      )}

      {createFirmName !== null && (
        <CreateFirmMiniModal
          initialName={createFirmName}
          onCancel={() => setCreateFirmName(null)}
          onCreatedAndLinked={async (firm) => {
            // Optimistically add the chip, then link via the mutation
            qc.setQueryData(['interaction', iid], old => old ? {
              ...old,
              interaction_firms: [
                ...(old.interaction_firms ?? []),
                { firm: { firm_id: firm.firm_id, name: firm.name } },
              ],
            } : old)
            await api.post(`/api/interactions/${iid}/firms/${firm.firm_id}`)
            await qc.invalidateQueries({ queryKey: ['interaction', iid] })
            await qc.invalidateQueries({ queryKey: ['firms'] })
            setCreateFirmName(null)
            showFlash('Firm created and linked')
          }}
        />
      )}

      {creatingPipelineEntry && (
        <CreatePipelineEntryMiniModal
          mandateId={mandateId}
          firms={firms}
          defaultFirmId={linkedFirms.length === 1 ? linkedFirms[0].firm_id : ''}
          onCancel={() => setCreatingPipelineEntry(false)}
          onCreated={handlePipelineEntryCreated}
        />
      )}
    </Modal>
  )
}

// ── Log interaction modal ─────────────────────────────────────────────────

function LogInteractionModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    type: 'Meeting', direction: 'Outbound', source: 'Manual',
    subject_title: '', body_notes: '',
    date: new Date().toISOString().slice(0, 16),
    follow_up_due: '', mandate_ids: [], contact_ids: [],
  })
  const [submitting, setSub] = useState(false)
  const [err, setErr] = useState(null)
  const qc = useQueryClient()

  const { data: mandatesData } = useQuery({
    queryKey: ['mandates'],
    queryFn: () => api.get('/api/mandates').then(r => r.data.data ?? []),
  })
  const { data: contactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })

  const mandates = mandatesData ?? []
  const allContacts = contactsData ?? []

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  function toggleMandate(id) {
    setForm(p => ({
      ...p,
      mandate_ids: p.mandate_ids.includes(id)
        ? p.mandate_ids.filter(m => m !== id)
        : [...p.mandate_ids, id],
    }))
  }


  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.date) return setErr('Date is required.')
    setSub(true); setErr(null)

    const payload = {
      type: form.type, source: form.source,
      date: new Date(form.date).toISOString(),
    }
    if (form.direction)     payload.direction     = form.direction
    if (form.subject_title) payload.subject_title = form.subject_title
    if (form.body_notes)    payload.body_notes    = form.body_notes
    if (form.follow_up_due) payload.follow_up_due = form.follow_up_due
    if (form.mandate_ids.length) payload.mandate_ids = form.mandate_ids

    try {
      const res = await api.post('/api/interactions', payload)
      const newId = res.data.data?.interaction_id
      if (newId && form.contact_ids.length > 0) {
        await Promise.all(
          form.contact_ids.map(cid =>
            api.post(`/api/interactions/${newId}/contacts/${cid}`)
          )
        )
      }
      qc.invalidateQueries({ queryKey: ['interactions'] })
      onCreated?.()
      onClose()
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setSub(false)
    }
  }

  return (
    <Modal title="Log Interaction" onClose={onClose} width={540}>
      <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <ErrorBanner msg={err} />

        {/* Type + Direction */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Type *</label>
            <select className="select" value={form.type} onChange={e => set('type', e.target.value)}>
              {INT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Direction</label>
            <select className="select" value={form.direction} onChange={e => set('direction', e.target.value)}>
              <option value="">None</option>
              {DIRECTIONS.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
        </div>

        {/* Subject */}
        <div className="input-group">
          <label className="input-label">Subject / Title</label>
          <input className="input" value={form.subject_title} placeholder="e.g. Intro call with LP team"
            onChange={e => set('subject_title', e.target.value)} />
        </div>

        {/* Notes */}
        <div className="input-group">
          <label className="input-label">Notes</label>
          <textarea className="textarea" rows={3} value={form.body_notes}
            placeholder="Key discussion points, next steps…"
            onChange={e => set('body_notes', e.target.value)} />
        </div>

        {/* Date + Source */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label className="input-label">Date *</label>
            <input className="input" type="datetime-local" value={form.date}
              onChange={e => set('date', e.target.value)} />
          </div>
          <div className="input-group">
            <label className="input-label">Source</label>
            <select className="select" value={form.source} onChange={e => set('source', e.target.value)}>
              {INT_SOURCES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Follow-up */}
        <div className="input-group">
          <label className="input-label">Follow-up Due</label>
          <input className="input" type="date" value={form.follow_up_due}
            onChange={e => set('follow_up_due', e.target.value)} />
        </div>

        {/* Mandates multi-select */}
        <div className="input-group">
          <label className="input-label">
            Linked Mandates{form.mandate_ids.length > 0 && ` (${form.mandate_ids.length})`}
          </label>
          <div style={{
            border: '1px solid var(--border)', borderRadius: 6,
            maxHeight: 140, overflow: 'auto', background: 'var(--surface-raised)',
          }}>
            {mandates.length === 0 ? (
              <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)' }}>
                No mandates found
              </div>
            ) : (
              mandates.map(m => {
                const checked = form.mandate_ids.includes(m.mandate_id)
                return (
                  <label key={m.mandate_id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', cursor: 'pointer',
                    borderBottom: '1px solid var(--border)',
                    background: checked ? 'var(--accent-muted)' : 'transparent',
                    transition: 'background 0.1s',
                  }}>
                    <input type="checkbox" checked={checked}
                      onChange={() => toggleMandate(m.mandate_id)}
                      style={{ accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }} />
                    <div style={{ fontSize: 13, fontWeight: checked ? 500 : 400 }}>{m.mandate_name}</div>
                  </label>
                )
              })
            )}
          </div>
        </div>

        {/* Contacts multi-select */}
        <div className="input-group">
          <label className="input-label">
            Participants{form.contact_ids.length > 0 && ` (${form.contact_ids.length})`}
          </label>
          <EntityMultiField kind="contact" records={allContacts} value={form.contact_ids}
            onChange={ids => setForm(p => ({ ...p, contact_ids: ids }))} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8,
          borderTop: '1px solid var(--border)', marginTop: 4 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Logging…' : 'Log Interaction'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function Interactions() {
  const [typeFilter, setTypeFilter]     = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [searchInput, setSearchInput]   = useState('')   // raw input value
  const [searchTerm, setSearchTerm]     = useState('')   // debounced, used in the query
  // Sort by follow_up_due (server-side). null = default (date desc); asc = soonest first.
  const [dueSort, setDueSort]           = useState(null)   // null | 'asc' | 'desc'
  const [page, setPage]                 = useState(1)

  // ── secondary filters (behind "+ More filters") — all server-side, same
  // pattern as type/source above ─────────────────────────────────────────
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [directionFilter, setDirectionFilter] = useState('')
  const [mandateFilter, setMandateFilter]     = useState('')
  const [contactFilter, setContactFilter]     = useState('')
  const [firmFilter, setFirmFilter]           = useState('')
  const [pipelineFilter, setPipelineFilter]   = useState('')   // '' | 'has' | 'none'
  const [dateFromFilter, setDateFromFilter]   = useState('')
  const [dateToFilter, setDateToFilter]       = useState('')
  const [senderInput, setSenderInput]         = useState('')   // raw
  const [senderFilter, setSenderFilter]       = useState('')   // debounced

  useEffect(() => {
    const t = setTimeout(() => setSenderFilter(senderInput.trim()), 300)
    return () => clearTimeout(t)
  }, [senderInput])
  const [selected, setSelected]         = useState(null)
  // Persists after the detail modal closes, unlike `selected` — lets the row
  // stay visibly marked as "the one you were just looking at" so you can find
  // your place again in a long list. Cleared only when a different row is
  // opened, not on modal close.
  const [lastInteractedId, setLastInteractedId] = useState(null)
  const [logOpen, setLogOpen]           = useState(false)
  const [toDelete, setToDelete]         = useState(null)
  // ── bulk-delete state ──────────────────────────────────────────────────
  const [selectedIds, setSelectedIds]   = useState(new Set())
  const [bulkOpen, setBulkOpen]         = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [bulkResult, setBulkResult]     = useState(null)   // { ok, failed }
  const qc = useQueryClient()

  // Debounce the search box so it doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  // Filters/sort changing invalidates the current page of results.
  useEffect(() => { setPage(1) }, [
    typeFilter, sourceFilter, searchTerm, dueSort,
    directionFilter, mandateFilter, contactFilter, firmFilter,
    pipelineFilter, dateFromFilter, dateToFilter, senderFilter,
  ])

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/interactions/${id}`).then(r => r.data),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['interactions'] })
      setToDelete(null)
      setSelected(prev => prev?.interaction_id === id ? null : prev)
    },
  })

  // Same filters for both tables. Upcoming (date after now) and Past are two
  // views of the same interactions, split only for display; Past is paged.
  const filterKey = {
    type: typeFilter, source: sourceFilter, search: searchTerm, dueSort,
    direction: directionFilter, mandate: mandateFilter, contact: contactFilter,
    firm: firmFilter, pipeline: pipelineFilter, dateFrom: dateFromFilter,
    dateTo: dateToFilter, sender: senderFilter,
  }
  const filterParams = {
    type:          typeFilter      || undefined,
    source:        sourceFilter    || undefined,
    search:        searchTerm      || undefined,
    sort:          dueSort ? `due_${dueSort}` : undefined,
    direction:     directionFilter || undefined,
    mandate_id:    mandateFilter   || undefined,
    contact_id:    contactFilter   || undefined,
    firm_id:       firmFilter      || undefined,
    has_pipeline:  pipelineFilter === 'has' ? 'true' : pipelineFilter === 'none' ? 'false' : undefined,
    date_from:     dateFromFilter  || undefined,
    date_to:       dateToFilter    || undefined,
    sender_email:  senderFilter    || undefined,
  }

  const { data: upcomingData, isLoading: upcomingLoading } = useQuery({
    queryKey: ['interactions', 'list', 'upcoming', filterKey],
    queryFn: () => api.get('/api/interactions', {
      // Soonest first, so a capped result keeps the nearest ones; flipped back
      // to reverse-chron for display like Past. A Follow-up Due sort wins.
      params: {
        ...filterParams, when: 'upcoming', limit: UPCOMING_LIMIT, offset: 0,
        sort: dueSort ? `due_${dueSort}` : 'date_asc',
      },
    }).then(r => {
      const rows = r.data.data ?? []
      return { items: dueSort ? rows : [...rows].reverse(), total: r.data.meta?.total ?? 0 }
    }),
    placeholderData: keepPreviousData,
  })

  const { data: pageData, isLoading: pastLoading, isPlaceholderData } = useQuery({
    queryKey: ['interactions', 'list', 'past', { ...filterKey, page }],
    queryFn: () => api.get('/api/interactions', {
      params: { ...filterParams, when: 'past', limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
    }).then(r => ({ items: r.data.data ?? [], total: r.data.meta?.total ?? 0 })),
    placeholderData: keepPreviousData,
  })
  const isLoading = upcomingLoading || pastLoading

  // Dropdown data for the mandate/contact/firm filters — shared cache keys
  // with the create/detail modals elsewhere in this file.
  const { data: filterMandatesData } = useQuery({
    queryKey: ['mandates'],
    queryFn: () => api.get('/api/mandates').then(r => r.data.data ?? []),
  })
  const filterMandates = filterMandatesData ?? []
  const { data: filterContactsData } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/api/contacts').then(r => r.data.data ?? []),
  })
  const filterContacts = filterContactsData ?? []
  const { data: filterFirmsData } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const filterFirms = filterFirmsData ?? []

  const upcomingItems = useMemo(() => upcomingData?.items ?? [], [upcomingData])
  const upcomingTotal = upcomingData?.total ?? 0
  const items      = useMemo(() => pageData?.items ?? [], [pageData])
  const pastTotal  = pageData?.total ?? 0
  const total      = upcomingTotal + pastTotal
  const totalPages = Math.max(1, Math.ceil(pastTotal / PAGE_SIZE))
  const isFiltered = !!(
    typeFilter || sourceFilter || searchTerm || directionFilter || mandateFilter ||
    contactFilter || firmFilter || pipelineFilter || dateFromFilter || dateToFilter || senderFilter
  )

  function clearAllFilters() {
    setTypeFilter(''); setSourceFilter(''); setSearchInput(''); setSearchTerm('')
    setDirectionFilter(''); setMandateFilter(''); setContactFilter(''); setFirmFilter('')
    setPipelineFilter(''); setDateFromFilter(''); setDateToFilter('')
    setSenderInput(''); setSenderFilter('')
  }

  // ── checkbox selection (operates on the current page's rows) ─────────────
  // Clear selection whenever filters, sort, or page change.
  useEffect(() => { setSelectedIds(new Set()) }, [
    typeFilter, sourceFilter, searchTerm, dueSort, page,
    directionFilter, mandateFilter, contactFilter, firmFilter,
    pipelineFilter, dateFromFilter, dateToFilter, senderFilter,
  ])

  function toggleCheck(id, e) {
    e.stopPropagation()
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  // Select-all toggles only the rows of the table whose header was clicked.
  function toggleCheckAll(rows) {
    const ids = rows.map(i => i.interaction_id)
    const allChecked = ids.length > 0 && ids.every(id => selectedIds.has(id))
    setSelectedIds(prev => {
      const next = new Set(prev)
      ids.forEach(id => allChecked ? next.delete(id) : next.add(id))
      return next
    })
  }

  const selectedRecords = useMemo(
    () => [...upcomingItems, ...items].filter(i => selectedIds.has(i.interaction_id)),
    [upcomingItems, items, selectedIds]
  )

  async function bulkDelete() {
    setBulkDeleting(true)
    setBulkResult(null)
    let ok = 0, failed = 0
    for (const id of selectedIds) {
      try { await api.delete(`/api/interactions/${id}`); ok++ }
      catch { failed++ }
    }
    await qc.invalidateQueries({ queryKey: ['interactions'] })
    qc.invalidateQueries({ queryKey: ['action-items', 'upcoming'] })
    setSelectedIds(new Set())
    setBulkDeleting(false)
    setBulkOpen(false)
    if (failed > 0) setBulkResult({ ok, failed })
  }

  // Upcoming and Past render the exact same columns.
  function renderTable(rows, empty, dimmed = false) {
    return (
          <div className="table-wrapper" style={{ opacity: dimmed ? 0.6 : 1, transition: 'opacity 0.1s' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36, paddingRight: 0 }} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                      checked={rows.length > 0 && rows.every(i => selectedIds.has(i.interaction_id))}
                      ref={el => { if (el) { const ids = rows.map(i => i.interaction_id); el.indeterminate = ids.some(id => selectedIds.has(id)) && !ids.every(id => selectedIds.has(id)) } }}
                      onChange={() => toggleCheckAll(rows)}
                      title="Select all"
                    />
                  </th>
                  <th>Type</th>
                  <th>Subject / Title</th>
                  <th>Direction</th>
                  <th>Mandate</th>
                  <th>Pipeline Entry</th>
                  <th>Action Items</th>
                  <th>Date</th>
                  <th
                    onClick={() => setDueSort(s => s === 'asc' ? 'desc' : 'asc')}
                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                    title="Sort by follow-up due date"
                  >
                    Follow-up Due {dueSort === 'asc' ? '▲' : dueSort === 'desc' ? '▼' : <span style={{ opacity: 0.25 }}>▲</span>}
                  </th>
                  <th>Source</th>
                  <th>Sent From</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={12}>
                    <div className="empty-state" style={{ padding: '40px 20px' }}>
                      <h3>{empty.title}</h3>
                      {empty.body && <p>{empty.body}</p>}
                    </div>
                  </td></tr>
                ) : (
                  rows.map(i => {
                    const overdue = isOverdue(i.follow_up_due)
                    return (
                      <tr
                        key={i.interaction_id}
                        style={{
                          cursor: 'pointer',
                          background: selectedIds.has(i.interaction_id) ? 'var(--accent-muted)' : undefined,
                          borderLeft: lastInteractedId === i.interaction_id ? '3px solid var(--accent)' : '3px solid transparent',
                        }}
                        onClick={() => { setSelected(i); setLastInteractedId(i.interaction_id) }}
                      >
                        <td style={{ width: 36, paddingRight: 0 }} onClick={e => toggleCheck(i.interaction_id, e)}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(i.interaction_id)}
                            onChange={e => toggleCheck(i.interaction_id, e)}
                            onClick={e => e.stopPropagation()}
                            style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                          />
                        </td>
                        <td>
                          <span className={`badge ${TYPE_BADGE[i.type] ?? 'badge-gray'}`}>{i.type}</span>
                        </td>
                        <td style={{ maxWidth: 260 }}>
                          <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap', fontSize: 13 }}>
                            {i.subject_title || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(no subject)</span>}
                          </div>
                        </td>
                        <td>
                          {i.direction
                            ? <span className={`badge ${DIR_BADGE[i.direction] ?? 'badge-gray'}`}
                                style={{ fontSize: 10 }}>{i.direction}</span>
                            : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                          {(i.interaction_mandates ?? []).length
                            ? i.interaction_mandates.map(im => im.mandate?.mandate_name).filter(Boolean).join(', ')
                            : '—'}
                        </td>
                        <td onClick={e => i.pipeline && e.stopPropagation()} style={{ fontSize: 12 }}>
                          {i.pipeline
                            ? <Link to={`/mandates/${i.pipeline.mandate_id}`}
                                style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, textDecoration: 'none' }}>
                                <span className={`badge ${PL_BADGE[i.pipeline.stage] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>
                                  {i.pipeline.stage}
                                </span>
                                {i.pipeline.investor_firm?.name && (
                                  <span style={{ color: 'var(--text-secondary)' }}>{i.pipeline.investor_firm.name}</span>
                                )}
                              </Link>
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                        <td>
                          {(() => {
                            const open = (i.action_items ?? []).filter(a => !a.completed).length
                            return (
                              <span className={`badge ${open > 0 ? 'badge-blue' : 'badge-gray'}`}
                                style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                                {open} open
                              </span>
                            )
                          })()}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                          {formatDateTime(i.date)}
                        </td>
                        <td>
                          {i.follow_up_due
                            ? <span style={{
                                fontSize: 12, fontWeight: overdue ? 600 : 400,
                                color: overdue ? 'var(--danger)' : 'var(--text-secondary)',
                                whiteSpace: 'nowrap',
                              }}>
                                {formatDate(i.follow_up_due)}{overdue && ' ⚑'}
                              </span>
                            : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                        </td>
                        <td>
                          <span className={`badge ${SOURCE_BADGE[i.source] ?? 'badge-gray'}`}
                            style={{ fontSize: 10 }}>{i.source}</span>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {i.sender_email || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                        <td onClick={e => e.stopPropagation()} style={{ width: 36, paddingRight: 8 }}>
                          <button
                            onClick={() => setToDelete(i)}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                              color: 'var(--text-muted)', padding: '3px 5px', borderRadius: 4,
                              display: 'inline-flex', alignItems: 'center' }}
                            onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                            title="Delete interaction"
                          ><TrashIcon /></button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
    )
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-text">
          <h1>Interactions</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {selectedIds.size >= 1 && (
            <button className="btn btn-danger" onClick={() => { setBulkResult(null); setBulkOpen(true) }}>
              Delete Selected ({selectedIds.size})
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setLogOpen(true)}>+ Log Interaction</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: showMoreFilters ? 10 : 16, flexWrap: 'wrap' }}>
        <input
          className="input"
          type="text"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search subject, notes, contacts, firms, mandate…"
          style={{ maxWidth: 320, flex: '1 1 260px' }}
        />
        <select className="select" style={{ maxWidth: 180 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {INT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="select" style={{ maxWidth: 200 }} value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
          <option value="">All sources</option>
          {INT_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          className={`btn btn-sm ${showMoreFilters ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setShowMoreFilters(v => !v)}
        >
          {showMoreFilters ? '− Fewer filters' : '+ More filters'}
        </button>
        {isFiltered && (
          <button className="btn btn-ghost btn-sm" onClick={clearAllFilters}>
            Clear filters
          </button>
        )}
        {isFiltered && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>
            {total} result{total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {showMoreFilters && (
        <div style={{
          display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16,
          padding: '12px', border: '1px solid var(--border)', borderRadius: 8,
          background: 'var(--surface-raised)',
        }}>
          <select className="select" style={{ maxWidth: 160 }} value={directionFilter} onChange={e => setDirectionFilter(e.target.value)}>
            <option value="">All directions</option>
            {DIRECTIONS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select className="select" style={{ maxWidth: 220 }} value={mandateFilter} onChange={e => setMandateFilter(e.target.value)}>
            <option value="">All mandates</option>
            {filterMandates.map(m => <option key={m.mandate_id} value={m.mandate_id}>{m.mandate_name}</option>)}
          </select>
          <select className="select" style={{ maxWidth: 220 }} value={contactFilter} onChange={e => setContactFilter(e.target.value)}>
            <option value="">All contacts</option>
            {filterContacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.name}</option>)}
          </select>
          <select className="select" style={{ maxWidth: 220 }} value={firmFilter} onChange={e => setFirmFilter(e.target.value)}>
            <option value="">All firms</option>
            {filterFirms.map(f => <option key={f.firm_id} value={f.firm_id}>{f.name}</option>)}
          </select>
          <select className="select" style={{ maxWidth: 200 }} value={pipelineFilter} onChange={e => setPipelineFilter(e.target.value)}>
            <option value="">Pipeline entry: any</option>
            <option value="has">Has pipeline entry</option>
            <option value="none">No pipeline entry</option>
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>From</label>
            <input type="date" className="input" style={{ maxWidth: 150 }}
              value={dateFromFilter} onChange={e => setDateFromFilter(e.target.value)} />
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>To</label>
            <input type="date" className="input" style={{ maxWidth: 150 }}
              value={dateToFilter} onChange={e => setDateToFilter(e.target.value)} />
          </div>
          <input
            className="input"
            type="text"
            value={senderInput}
            onChange={e => setSenderInput(e.target.value)}
            placeholder="Sender email contains…"
            style={{ maxWidth: 220 }}
          />
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-lg" />
        </div>
      ) : (
        <>
          <h2 style={SECTION_HEADING_STYLE}>Upcoming{upcomingTotal ? ` (${upcomingTotal})` : ''}</h2>
          {renderTable(upcomingItems, {
            title: isFiltered ? 'No upcoming interactions match your filters' : 'No upcoming interactions',
          })}
          {upcomingTotal > upcomingItems.length && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>
              Showing the {upcomingItems.length} soonest of {upcomingTotal} upcoming. Narrow with filters to see the rest.
            </p>
          )}

          <h2 style={{ ...SECTION_HEADING_STYLE, marginTop: 28 }}>Past{pastTotal ? ` (${pastTotal})` : ''}</h2>
          {renderTable(items, {
            title: isFiltered ? 'No past interactions match your filters' : 'No interactions yet',
            body: isFiltered ? null : 'Log your first interaction to get started.',
          }, isPlaceholderData)}
        </>
      )}

      {!isLoading && <Pagination page={page} totalPages={totalPages} onChange={setPage} />}

      {/* Log interaction modal */}
      {logOpen && (
        <LogInteractionModal
          onClose={() => setLogOpen(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['interactions'] })}
        />
      )}

      {/* Detail modal */}
      {selected && (
        <InteractionDetailModal
          interaction={selected}
          onClose={() => setSelected(null)}
        />
      )}

      {/* Delete confirm */}
      {toDelete && (
        <DeleteConfirmModal
          name={toDelete.subject_title || `${toDelete.type} interaction`}
          onCancel={() => setToDelete(null)}
          onConfirm={() => deleteMutation.mutate(toDelete.interaction_id)}
          deleting={deleteMutation.isPending}
        />
      )}

      {/* Bulk-delete confirmation */}
      {bulkOpen && (
        <div
          onClick={e => e.target === e.currentTarget && !bulkDeleting && setBulkOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1200, padding: 16,
          }}
        >
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border-light)',
            borderRadius: 10, width: '100%', maxWidth: 460,
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
          }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
              Delete {selectedIds.size} Interaction{selectedIds.size !== 1 ? 's' : ''}?
            </h2>

            <ul style={{ margin: '0 0 14px', padding: '0 0 0 18px', fontSize: 13, color: 'var(--text-primary)' }}>
              {selectedRecords.slice(0, 10).map(i => (
                <li key={i.interaction_id} style={{ marginBottom: 2 }}>
                  {i.subject_title || `${i.type} interaction`}
                </li>
              ))}
              {selectedRecords.length > 10 && (
                <li style={{ color: 'var(--text-secondary)' }}>…and {selectedRecords.length - 10} more</li>
              )}
            </ul>

            <div style={{
              background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 6, padding: '10px 14px', fontSize: 12,
              color: 'var(--danger-text)', marginBottom: 20, lineHeight: 1.5,
            }}>
              This action cannot be undone. These interactions will be permanently removed
              including any linked contacts and action items.
            </div>

            {bulkResult && (
              <div style={{
                background: 'var(--danger-muted)', borderRadius: 6,
                padding: '8px 12px', fontSize: 12, color: 'var(--danger-text)', marginBottom: 14,
              }}>
                Deleted {bulkResult.ok}, failed {bulkResult.failed}. The failed rows may have
                dependent data — try again or remove them individually.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setBulkOpen(false)} disabled={bulkDeleting}>Cancel</button>
              <button className="btn btn-danger" onClick={bulkDelete} disabled={bulkDeleting}>
                {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size} Interaction${selectedIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
